/**
 * Resolve a video's duration (seconds) for the thumbnail badge, cached across
 * the app like `useVideoTitle`. Unlike titles (oEmbed — unmetered, one fetch
 * per video), duration costs YouTube Data API quota server-side, so requests
 * are coalesced: every id asked for within the same tick is batched into one
 * `/youtube/durations` call (chunked at 50 ids, matching the handler's cap).
 *
 * Returns null until resolved, on any failure, or when the video has no
 * duration (e.g. a live stream, or no `YT_API_KEY` configured) — callers
 * simply omit the badge.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { videoId } from "../lib/youtube";

const CHUNK_SIZE = 50;

const cache = new Map<string, number>();
const inflight = new Map<string, Promise<number | null>>();
const resolvers = new Map<string, (seconds: number | null) => void>();
let pending = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(flush, 0);
}

async function flush() {
  flushTimer = null;
  const ids = [...pending];
  pending = new Set();
  if (ids.length === 0) return;

  const durations: Record<string, number> = {};
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    const chunk = ids.slice(i, i + CHUNK_SIZE);
    try {
      const res = await api.videoDurations(chunk);
      Object.assign(durations, res.durations);
    } catch {
      /* best-effort — ids in this chunk resolve to null below */
    }
  }

  for (const id of ids) {
    const seconds = durations[id];
    if (seconds !== undefined) cache.set(id, seconds);
    inflight.delete(id);
    const resolve = resolvers.get(id);
    resolvers.delete(id);
    resolve?.(seconds ?? null);
  }
}

function ensureRequested(id: string): Promise<number | null> {
  const cached = cache.get(id);
  if (cached !== undefined) return Promise.resolve(cached);

  const existing = inflight.get(id);
  if (existing) return existing;

  const promise = new Promise<number | null>((resolve) => resolvers.set(id, resolve));
  inflight.set(id, promise);
  pending.add(id);
  scheduleFlush();
  return promise;
}

export function useVideoDuration(url: string): number | null {
  const key = videoId(url) ?? "";
  const [seconds, setSeconds] = useState<number | null>(() => cache.get(key) ?? null);

  useEffect(() => {
    if (!key) {
      setSeconds(null);
      return;
    }
    const cached = cache.get(key);
    if (cached !== undefined) {
      setSeconds(cached);
      return;
    }

    let active = true;
    ensureRequested(key).then((s) => {
      if (active) setSeconds(s);
    });

    return () => {
      active = false;
    };
  }, [key]);

  return seconds;
}
